import { useEffect, useState } from 'react';
import {
    useGetMemberValidationMutation,
    useSearchMemberMutation,
    useToggleArchiveMemberValidationMutation,
    usePutMemberValidationMutation
} from '@services/member/member-validation/member-validation.endpoints';
import { useNavigate, useParams } from 'react-router-dom';
import { MemberValidation } from '@services/member/member-validation/member-validation.types';
import { Contribution, Member } from '@services/member/member/member.types';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { createColumnHelper } from '@tanstack/react-table';

export const useEditionPageVM = () => {
    const { id } = useParams();
    const [memberValidation, setMemberValidation] = useState<MemberValidation>();
    const [foundMember, setFoundMember] = useState<Member | undefined>(undefined);
    const [selectedMember, setSelectedMember] = useState<Member | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const contributionColumnHelper = createColumnHelper<Contribution>();

    const MySwal = withReactContent(Swal);

    const [
        toggleArchiveMemberValidation,
        {
            isLoading: isToggleArchiveMemberValidationLoading,
            isError: isToggleArchiveMemberValidationError,
            error: toggleArchiveMemberValidationError,
            isSuccess: isToggleArchiveMemberValidationSuccess,
            data: toggleArchiveMemberValidationResponse
        }
    ] = useToggleArchiveMemberValidationMutation();

    const [
        putMemberValidation,
        {
            isLoading: isPutMemberValidationLoading,
            isError: isPutMemberValidationError,
            error: putMemberValidationError,
            isSuccess: isPutMemberValidationSuccess,
            data: putMemberValidationResponse
        }
    ] = usePutMemberValidationMutation();

    const handleArchive = (memberValidation: MemberValidation) => {
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir archiver cette demande ?',
            text: "Si vous archivez cette demande, l'adhérent ne pourra pas avoir accès au site web.",
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Archiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                if (memberValidation.id) {
                    toggleArchiveMemberValidation({ id: memberValidation.id as string | number, archived: true });
                }
            }
        });
    };

    const handleActivation = (memberValidation: MemberValidation) => {
        MySwal.fire({
            title: "Êtes-vous sûr de vouloir activer l'accès à cet adhérent ?",
            text: "Si vous acceptez cette demande, l'adhérent pourra avoir accès au site web.",
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Activer',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                if (memberValidation.id && selectedMember?.id) {
                    putMemberValidation({ id: memberValidation.id.toString(), memberId: selectedMember.id.toString() });
                }
            }
        });
    };

    const [
        searchMember,
        {
            isLoading: isSearchMemberLoading,
            isError: isSearchMemberError,
            error: searchMemberError,
            isSuccess: isSearchMemberSuccess,
            data: searchMemberResponse
        }
    ] = useSearchMemberMutation();

    const [
        getMemberValidation,
        {
            isLoading: isGetMemberValidationLoading,
            isError: isGetMemberValidationError,
            error: getMemberValidationError,
            isSuccess: isGetMemberValidationSuccess,
            data: getMemberValidationResponse
        }
    ] = useGetMemberValidationMutation();

    useEffect(() => {
        getMemberValidation({ id: id as string });
    }, [id]);

    useEffect(() => {
        if (!memberValidation) return;
        searchMember({ data: { cosemNumber: memberValidation.cosemNumber, email: memberValidation.email } });
    }, [memberValidation]);

    useEffect(() => {
        if (foundMember && !selectedMember) {
            setSelectedMember(foundMember);
        }
    }, [foundMember]);

    useEffect(() => {
        if (isGetMemberValidationLoading) return;
        if (isGetMemberValidationError && getMemberValidationError) return;
        if (isGetMemberValidationSuccess && getMemberValidationResponse) {
            setMemberValidation(getMemberValidationResponse.data);
        }
    }, [
        isGetMemberValidationError,
        isGetMemberValidationSuccess,
        getMemberValidationError,
        getMemberValidationResponse,
        isGetMemberValidationLoading
    ]);

    useEffect(() => {
        if (isToggleArchiveMemberValidationLoading) return;
        if (isToggleArchiveMemberValidationError && toggleArchiveMemberValidationError) return;
        if (isToggleArchiveMemberValidationSuccess && toggleArchiveMemberValidationResponse) {
            navigate('/products-management/members-validation');
        }
    }, [
        isToggleArchiveMemberValidationError,
        isToggleArchiveMemberValidationSuccess,
        toggleArchiveMemberValidationError,
        toggleArchiveMemberValidationResponse,
        isToggleArchiveMemberValidationLoading
    ]);

    useEffect(() => {
        if (isPutMemberValidationLoading) return;
        if (isPutMemberValidationError && putMemberValidationError) return;
        if (isPutMemberValidationSuccess && putMemberValidationResponse) {
            navigate('/products-management/members-validation');
        }
    }, [
        isPutMemberValidationError,
        isPutMemberValidationSuccess,
        putMemberValidationError,
        putMemberValidationResponse,
        isPutMemberValidationLoading
    ]);

    useEffect(() => {
        if (isSearchMemberLoading) return;
        if (isSearchMemberError) return;
        if (searchMemberError) return;
        if (isSearchMemberSuccess && searchMemberResponse) {
            setFoundMember(searchMemberResponse.data ?? undefined);
            setIsLoading(false);
        }
    }, [isSearchMemberError, isSearchMemberSuccess, searchMemberError, searchMemberResponse, isSearchMemberLoading]);

    return {
        memberValidation,
        foundMember,
        isGetMemberValidationLoading,
        isSearchMemberLoading,
        isLoading,
        selectedMember,
        setSelectedMember,
        handleArchive,
        handleActivation,
        contributionColumnHelper
    };
};
